import React, { type ComponentProps, forwardRef, type Ref } from 'react';
import type { TextInstance } from 'react-native';

import { webOnlyStyles } from '../../../utils/webOnlyStyles';
import { BodyText } from '../BodyText';
import { highlightWords } from './HighlightWords.helpers';

import type { DisplayText } from '../DisplayText';
import type { LabelText } from '../LabelText';
import type { TitleText } from '../TitleText';

// ─────────────────────────────────────────────────────────────────────────────

export const HighlightWords = forwardRef(
  <ComponentType extends TextComponent = typeof BodyText>(
    props: HighlightWordsProps<ComponentType>,
    ref: Ref<TextInstance> | undefined,
  ) => {
    const { as = BodyText, text, style, highlights = [], ...restProps } = props;

    const Component = as as React.ComponentType<ComponentProps<ComponentType>>;

    const castedProps = restProps as ComponentProps<typeof Component>;
    const substrings = highlightWords(text, highlights);

    const styles = [webStyles, style];

    // ─────────────────────────────────────────────────────────────────────

    if (substrings.length === 1) {
      const [{ substring, shouldHighlight }] = substrings;

      return (
        <Component style={styles} bold={shouldHighlight} {...castedProps}>
          {substring}
        </Component>
      );
    }

    // ─────────────────────────────────────────────────────────────────────
    return (
      <Component style={styles} ref={ref} {...castedProps}>
        {substrings.map(({ key, substring, shouldHighlight }) => (
          <Component style={styles} key={key} bold={shouldHighlight} {...castedProps}>
            {substring}
          </Component>
        ))}
      </Component>
    );
  },
);

// ─── Styles ──────────────────────────────────────────────────────────────────

const webStyles = webOnlyStyles({
  display: 'inline' as never,
});

// ─── Types ───────────────────────────────────────────────────────────────────

type TextComponent = typeof BodyText | typeof DisplayText | typeof TitleText | typeof LabelText;

type HighlightWordsProps<ComponentType extends TextComponent = typeof BodyText> = {
  text: string;
  highlights?: readonly string[];

  /** Polymorphic component to render as */
  as?: ComponentType;
} & ComponentBaseProps<ComponentType>;

type ComponentBaseProps<ComponentType extends TextComponent> = Omit<
  ComponentProps<ComponentType>,
  'children' | 'bold' | 'ref'
>;
